import { Box } from '@mui/material';
import { useField, useFormikContext } from 'formik';
import type { FC } from 'react';

import { getTenantUnits } from '@core/configuration';
import type { Address } from '@core/types/api/Address/Address';
import type { LocationResult } from '@core/types/api/LocationResult/LocationResult';
import { requiredLabel } from '@core/utils/strings';
import { countryOptions, resolveAddressCountry, withCurrentOption } from '@core/utils/subdivisions';
import type { ExcludeMethodsDeep } from '@core/utils/types';

import { Input } from '../Input/Input';
import { InputDropdown } from '../InputDropdown/InputDropdown';
import { AddressLine } from './AddressLine';
import { useAddress } from './hooks/useAddress';
import { SubdivisionField } from './SubdivisionField';

interface AddressFieldProps {
  name: string;
  isCityRequired?: boolean;
  isRequired?: boolean;
  onAddressChange?(address: ExcludeMethodsDeep<Address>): void;
}

export const AddressField: FC<AddressFieldProps> = ({
  name,
  isRequired = false,
  isCityRequired = false,
  onAddressChange,
}) => {
  const { handleAddressChange } = useAddress({ name });
  const { setFieldValue } = useFormikContext();
  const [countryField] = useField<string | null>(`${name}.country`);

  const country = resolveAddressCountry(countryField.value, getTenantUnits().locale);

  const handleChange = (location: LocationResult): void => {
    const address = handleAddressChange(location);

    onAddressChange?.(address);
  };

  // A subdivision only means something within its country, and some codes mean
  // different places in different ones — 'WA' is Washington or Western
  // Australia. Only fires on the user picking a country, so values loaded from
  // the server are still shown as-is.
  const handleCountrySelect = async (selected: string): Promise<void> => {
    if (selected === countryField.value) return;

    await setFieldValue(`${name}.state`, '');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
        <AddressLine
          name={`${name}.line1`}
          label={requiredLabel('Address Line 1', isRequired)}
          onAddressChange={handleChange}
        />
        <Input name={`${name}.line2`} label='Line 2' />
      </Box>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Input name={`${name}.city`} label={requiredLabel('City', isCityRequired || isRequired)} />
        <SubdivisionField name={name} country={country} isRequired />
      </Box>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Input name={`${name}.postalCode`} label={requiredLabel('Postal Code', isRequired)} />
        <InputDropdown
          name={`${name}.country`}
          label={requiredLabel('Country', isRequired)}
          options={withCurrentOption(countryOptions(), countryField.value)}
          onSelect={handleCountrySelect}
        />
      </Box>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Input
          type='number'
          name={`${name}.latitude`}
          label={requiredLabel('Latitude', isRequired)}
        />
        <Input
          type='number'
          name={`${name}.longitude`}
          label={requiredLabel('Longitude', isRequired)}
        />
      </Box>
    </Box>
  );
};
